import jsZip from "jszip";
import ReactDOMServer from "react-dom/server";
import Highcharts, { ExportingOptions, Options } from "highcharts";

async function createZipFromSvg({ svgArr }: { svgArr: string[] }) {
    const zip = jsZip();
    const parser = new DOMParser();
    let lastTitle: string = "";

    svgArr.map((image: string, i: number) => {
        let parseImage = parser.parseFromString(image, "image/svg+xml"),
            getWrapperSvg = parseImage.querySelector("svg"),
            getTitle = parseImage.querySelector("title"),
            getWrapperHeightLegend = parseImage.querySelector("g#custom_legend"),
            getWrapperBackground = parseImage.querySelector(".highcharts-background"),
            heightLegend = getWrapperHeightLegend?.getAttribute("height"),
            currentTitle = getTitle?.textContent,
            xml = new XMLSerializer();

        // Modification du SVG pour intégration des légendes
        if (getWrapperHeightLegend !== null) {
            /* @ts-ignore */
            const fullHeight = String(parseInt(getWrapperSvg?.getAttribute("height")) + parseFloat(heightLegend)) || "400";
            getWrapperSvg?.setAttribute("height", fullHeight);
            getWrapperSvg?.setAttribute("viewBox", `0 0 ${getWrapperSvg?.getAttribute("width")} ${fullHeight}`);
            getWrapperBackground?.setAttribute("height", fullHeight);
        }

        if (!currentTitle) {
            currentTitle = lastTitle;
        }

        lastTitle = currentTitle.replace(/[/&\\#,+()$~%.'":*?<>{}]/g, "_");
        return zip.file(`${i}_${lastTitle}.svg`, xml.serializeToString(parseImage));
    });

    zip.generateAsync({ type: "blob" }).then((zipFile: Blob) => {
        const currentDate = new Date();
        const fileName = `export_${currentDate.toLocaleDateString()}_${currentDate.getHours()}.${currentDate.getMinutes()}.zip`;
        download(zipFile, fileName);
    });
}

const download = (file: Blob, filename: string) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(file);
    link.download = filename;
    link.click();
    link.parentNode?.removeChild(link);
};

/**
 * Create a global exportCharts method that takes an array of charts as an argument,
 * and exporting options as the second argument
 */
export const exportCharts = (
    charts: Highcharts.Chart[],
    rawCharts: Highcharts.Options[],
    labelsData: any,
    options: ExportingOptions = {}
) => {
    // Si les options sont vides on exporte un PNG
    if (Object.keys(options).length) {
        options = Highcharts.merge(Highcharts.getOptions().exporting, options);
    }

    // Get SVG asynchronously and then download the resulting SVG
    getSVG(charts, options, rawCharts, labelsData, function (svg: string) {
        return;
    });
};

// Version avec modules highcharts hors ligne
const getSVG = (
    charts: Highcharts.Chart[],
    options: ExportingOptions,
    rawCharts: any[],
    labelsData: any,
    callback: Function,
    batchSize: number = 50 // Vous pouvez ajuster la taille du lot
) => {
    let svgArr: string[] = [];

    const addSVG = (svgres: any) => {
        svgArr.push(svgres);
    };

    // Encapsule l'exportation d'un seul graphique
    const exportSingleChart = async (chart: Highcharts.Chart, index: number) => {
        return new Promise<void>((resolve) => {
            /* @ts-ignore */
            chart.getSVGForLocalExport(
                options,
                {},
                function () {
                    console.log("Failed to get SVG for chart", index);
                    resolve(); // Pass to the next chart
                },
                function (svg: string) {
                    const chartOptions: Options & Partial<{ exportData: ExportDataInterface }> = chart?.options;
                    const exportData = chartOptions?.exportData;

                    if (exportData) {
                        let startHeight = 400;
                        const margin = 1;
                        const tableHeader = ReactDOMServer.renderToString(
                            drawRow(
                                margin,
                                (startHeight += 20),
                                {
                                    code: exportData.header.code,
                                    label: exportData.header.label,
                                    value: exportData.header.effectif,
                                },
                                true
                            )
                        );
                        startHeight += 20;
                        const tableData = exportData.labels.map((categorie: any, index: number) => {
                            const result = categorie.match(/^(.*)<br\/>n: (\d+)$/);
                            const { lines, numberOfLines } = splitLabelIntoLines(result[1], 120);
                            const rowStartHeight = startHeight;
                            startHeight += 20 * numberOfLines;
                            return ReactDOMServer.renderToString(
                                drawRow(margin, rowStartHeight, {
                                    code: index + 1,
                                    label: lines,
                                    value: result[2],
                                })
                            );
                        });

                        svg = svg.replace(
                            "</svg>",
                            `<title>${exportData.title}</title>` +
                                `<g id="custom_legend" height="${startHeight - 400}" width="800" viewBox="0 400 800 ${
                                    startHeight - 400
                                }">` +
                                tableHeader +
                                tableData.join("") +
                                "</g>" +
                                "</svg>"
                        );
                    }

                    addSVG(svg);
                    resolve(); // Proceed to the next chart
                }
            );
        });
    };

    // Exporter un lot de graphiques
    const exportBatch = async (startIndex: number) => {
        const endIndex = Math.min(startIndex + batchSize, charts.length);

        for (let i = startIndex; i < endIndex; i++) {
            await exportSingleChart(charts[i], i);
        }

        if (endIndex < charts.length) {
            // Pause pour éviter la surcharge
            setTimeout(() => exportBatch(endIndex), 1000);
        } else {
            // Finaliser le ZIP après le traitement de tous les graphiques
            await createZipFromSvg({ svgArr });
        }
    };

    exportBatch(0); // Démarrer à partir du premier graphique
};

const drawRow = (x: number, y: number, content: any, header: boolean = false) => {
    const widthCode = 49,
        widthLabel = 700,
        widthValue = 49,
        heightRow = 20;
    const numLines = Array.isArray(content.label) ? content.label.length : 1;
    const adjustedHeight = heightRow * numLines;

    return (
        <>
            {drawCell(x, y, widthCode, adjustedHeight, content.code, header)}
            {drawCell(x + widthCode, y, widthLabel, adjustedHeight, content.label, header)}
            {drawCell(x + widthCode + widthLabel, y, widthValue, adjustedHeight, content.value, header)}
        </>
    );
};

const drawCell = (
    x: number,
    y: number,
    width: number,
    height: number,
    content: string | number | string[],
    header: boolean = false
) => {
    const style = {
        fill: header ? "rgb(125, 125, 125)" : "rgb(255,255,255)",
        strokeWidth: 1,
        stroke: "rgb(0,0,0)",
    };
    const lineHeight = 15;
    const lines = Array.isArray(content) ? content : [content];

    return (
        <>
            <rect x={x} y={y} width={width} height={height} style={style} />
            {lines.map((line, i) => (
                <text fill="#000000" fontSize="11" fontFamily="Verdana" x={x + 5} y={y + i * lineHeight + lineHeight}>
                    {line}
                </text>
            ))}
        </>
    );
};

const splitLabelIntoLines = (label: string, maxLineLength: number) => {
    const lines: string[] = [];
    let line = "";

    label.split(" ").forEach((word: string) => {
        if (line.length + word.length > maxLineLength) {
            lines.push(line);
            line = word;
        } else {
            line += " " + word;
        }
    });
    lines.push(line);

    return {
        lines,
        numberOfLines: lines.length,
    };
};
